import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  Pencil,
  Plus,
  Trash2,
  Users,
  ListChecks,
  LayoutDashboard,
  Info,
  DollarSign,
  TrendingUp,
  AlertCircle,
  UserPlus,
  Receipt,
  ChevronDown,
  ExternalLink,
  Radio,
  FileText,
  Paperclip,
  Upload,
  MessageSquare,
  Send,
  AtSign,
  Loader2,
  Download,
  Eye,
  X,
} from 'lucide-react'
import { projectsApi, usersApi, departmentsApi, expensesApi, quotesApi, apiClient, getApiErrorMessage } from '@/services/api'
import { useAuthStore } from '@/stores/authStore'
import { useProjectContextStore } from '@/stores/projectContextStore'
import { useExpenseDraftStore } from '@/stores/expenseDraftStore'
import type { ExpenseFormData } from '@/stores/expenseDraftStore'
import { Badge } from '@/components/common/Badge'
import { Card, CardHeader } from '@/components/common/Card'
import { StatCard } from '@/components/common/StatCard'
import { Spinner } from '@/components/common/Spinner'
import { ProjectFormModal } from '@/components/projects/ProjectFormModal'
import { ControlRoomTab } from '@/components/projects/ControlRoomTab'
import { cn } from '@/lib/utils'
import { confirm } from '@/lib/confirm'
import { toast } from '@/lib/toast'
import RichNoteEditor from '@/components/common/RichNoteEditor'
import type { Project, ProjectStatus, ProjectType, ModuleType, Task, TaskStatus, TaskPriority, User, ExpenseCategory, Expense, QuoteStatus } from '@/types'

// ============================================================
// TYPES
// ============================================================

interface ProjectTeamMemberDetail {
  id: string
  projectId: string
  userId: string
  role: string
  createdAt: string
  user: {
    id: string
    firstName: string
    lastName: string
    avatar?: string
    role?: { name: string; level?: number }
  }
}

interface ProjectTask {
  id: string
  title: string
  status: TaskStatus
  priority: TaskPriority
  assignee?: {
    id: string
    firstName: string
    lastName: string
    avatar?: string
  }
  dueDate?: string
  createdAt: string
}

interface ProjectWithRelations extends Project {
  team?: ProjectTeamMemberDetail[]
  tasks?: ProjectTask[]
  _count?: {
    tasks: number
    tasksDone?: number
    team: number
  }
}

type TabId = 'overview' | 'team' | 'tasks' | 'expenses' | 'quotes' | 'details' | 'control-room'

// ============================================================
// CONSTANTS
// ============================================================

const TABS: { id: TabId; label: string; icon: typeof LayoutDashboard }[] = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'team', label: 'Team', icon: Users },
  { id: 'tasks', label: 'Task', icon: ListChecks },
  { id: 'expenses', label: 'Spese', icon: Receipt },
  { id: 'quotes', label: 'Preventivi', icon: FileText },
  { id: 'details', label: 'Dettagli', icon: Info },
]

const STATUS_VARIANT: Record<ProjectStatus, 'neutral' | 'info' | 'warning' | 'success' | 'danger'> = {
  DRAFT: 'neutral',
  PLANNING: 'info',
  ACTIVE: 'success',
  ON_HOLD: 'warning',
  COMPLETED: 'success',
  CANCELLED: 'danger',
}
const STATUS_LABEL: Record<ProjectStatus, string> = {
  DRAFT: 'Bozza',
  PLANNING: 'Pianificazione',
  ACTIVE: 'Attivo',
  ON_HOLD: 'In Pausa',
  COMPLETED: 'Completato',
  CANCELLED: 'Annullato',
}

const TYPE_LABEL: Record<ProjectType, string> = {
  // Social
  SOCIAL_MANAGEMENT: 'Gestione Social Media',
  SOCIAL_CAMPAIGN: 'Campagna Social',
  COMMUNITY_MANAGEMENT: 'Community Management',
  CONTENT_CREATION: 'Content Creation',
  // ADV
  ADV_CAMPAIGN: 'Campagna ADV',
  PERFORMANCE_MARKETING: 'Performance Marketing',
  AWARENESS_CAMPAIGN: 'Campagna Awareness',
  LEAD_GENERATION: 'Lead Generation',
  RETARGETING: 'Retargeting / Remarketing',
  INFLUENCER_MARKETING: 'Influencer Marketing',
  // Eventi
  EVENT_ORGANIZATION: 'Organizzazione Evento',
  FAIR_STAND: 'Fiera & Stand',
  CONFERENCE: 'Conferenza / Convegno',
  PRODUCT_LAUNCH: 'Lancio Prodotto',
  GALA_DINNER: 'Gala / Cena di Gala',
  WORKSHOP_SEMINAR: 'Workshop / Seminario',
  CORPORATE_EVENT: 'Evento Aziendale',
  PRESS_CONFERENCE: 'Conferenza Stampa',
  ROADSHOW: 'Roadshow',
  // Digital Hub
  WEBSITE: 'Sito Web',
  ECOMMERCE: 'E-commerce',
  SEO: 'SEO & Posizionamento',
  APP_WEBAPP: 'App & Webapp',
  CRM: 'CRM',
  ERP: 'ERP',
  CHATBOT: 'Chatbot',
  CHATBOT_VOICE: 'Chatbot Vocale',
  CHATBOT_AVATAR: 'Chatbot Avatar Animato',
  AUTOMATION: 'Automazione',
  // PR
  PRESS_OFFICE: 'Ufficio Stampa',
  MEDIA_RELATIONS: 'Media Relations',
  CRISIS_COMMUNICATION: 'Crisis Communication',
  INFLUENCER_RELATIONS: 'Influencer Relations',
  // Branding
  BRAND_IDENTITY: 'Brand Identity',
  REBRANDING: 'Rebranding',
  NAMING: 'Naming',
  PACKAGING_DESIGN: 'Packaging Design',
  // Consulenza
  STRATEGIC_CONSULTING: 'Consulenza Strategica',
  TRAINING: 'Formazione',
  MARKETING_AUDIT: 'Audit Marketing',
  MARKETING_PLAN: 'Piano Marketing',
  // Visual Production
  VIDEO_SPOT: 'Spot',
  VIDEO_CORPORATE: 'Video Corporate',
  VIDEO_DOCUFILM: 'Docufilm',
  VIDEO_REEL: 'Reel',
  VIDEO_INTERVIEW: 'Intervista',
  VIDEO_EVENT: 'Video Evento',
  PHOTO_SHOOTING: 'Shooting Fotografico',
}

const MODULE_LABEL: Record<ModuleType, string> = {
  SOCIAL: 'Social Media Management',
  ADV: 'Advertising & Campagne Paid',
  EVENTI: 'Organizzazione Eventi',
  DIGITAL: 'Digital Hub (Web, SEO, App)',
  PR: 'Pubbliche Relazioni & Ufficio Stampa',
  BRANDING: 'Branding & Identità Visiva',
  CONSULENZA: 'Consulenza Strategica',
  VISUAL_PRODUCTION: 'Visual Production',
  MERCHANDISING: 'Merchandising',
}

const MODULE_ROUTE: Record<string, string> = {
  SOCIAL: '/ped',
  ADV: '/adv',
  PR: '/pr',
  BRANDING: '/branding',
  CONSULENZA: '/consulenza',
  DIGITAL: '/digital',
  VISUAL_PRODUCTION: '/visual-production',
  MERCHANDISING: '/merchandising',
}

// Moduli che hanno una vista dedicata accessibile ai guest/clienti.
// Solo questi vengono mostrati nella schermata guest del progetto.
const GUEST_MODULE_TYPES = new Set<string>(['SOCIAL'])

const TASK_STATUS_COLORS: Record<TaskStatus, string> = {
  TODO: 'bg-slate-100 text-slate-600',
  IN_PROGRESS: 'bg-primary-50 text-primary-600',
  IN_REVIEW: 'bg-primary-50 text-primary-700',
  BLOCKED: 'bg-danger-50 text-danger-600',
  DONE: 'bg-success-50 text-success-700',
  CANCELLED: 'bg-slate-200 text-slate-500',
}
const TASK_STATUS_LABEL: Record<TaskStatus, string> = {
  TODO: 'Da Fare',
  IN_PROGRESS: 'In Corso',
  IN_REVIEW: 'In Revisione',
  BLOCKED: 'Bloccato',
  DONE: 'Fatto',
  CANCELLED: 'Annullato',
}

const PRIORITY_COLORS: Record<TaskPriority, string> = {
  LOW: 'text-slate-400',
  MEDIUM: 'text-primary-500',
  HIGH: 'text-warning-600',
  URGENT: 'text-danger-600',
}
const PRIORITY_LABEL: Record<TaskPriority, string> = {
  LOW: 'Bassa',
  MEDIUM: 'Media',
  HIGH: 'Alta',
  URGENT: 'Urgente',
}

const inputClass = cn(
  'w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg',
  'focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20',
  'placeholder:text-slate-400 transition-colors'
)

const EUR = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })

// ============================================================
// SKELETON
// ============================================================

function TabSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-3 animate-pulse">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-16 bg-slate-100 rounded-lg" />
      ))}
    </div>
  )
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-2 p-3 bg-danger-50 border border-danger-200 rounded-lg text-sm text-danger-700">
      <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
      {message}
    </div>
  )
}

// ============================================================
// PROGRESS BAR
// ============================================================

function ProgressBar({ label, value, max, color = 'accent', warnOnHigh = false }: { label: string; value: number; max: number; color?: string; warnOnHigh?: boolean }) {
  const pct = max > 0 ? Math.min(Math.round((value / max) * 100), 100) : 0
  const colorMap: Record<string, string> = {
    accent: 'bg-primary-500',
    success: 'bg-success-500',
    warning: 'bg-warning-500',
    danger: 'bg-danger-500',
  }
  // Il color-thresholding rosso/giallo ha senso solo quando un valore alto è negativo
  // (es. Budget: sforamento). Per metriche dove l'avanzamento alto è POSITIVO
  // (Task completati, Timeline) si usa il colore semantico crescente.
  const barColor = warnOnHigh
    ? (pct > 90 ? 'bg-danger-500' : pct > 70 ? 'bg-warning-500' : (colorMap[color] ?? 'bg-primary-500'))
    : (colorMap[color] ?? 'bg-primary-500')

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-medium text-slate-600">{label}</span>
        <span className="text-xs text-slate-500 tabular-nums">{pct}%</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className={cn('h-full rounded-full transition-all', barColor)} style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

// ============================================================
// MANUAL PROGRESS SLIDER
// ============================================================

function ManualProgressSlider({ project }: { project: ProjectWithRelations }) {
  const queryClient = useQueryClient()
  const [value, setValue] = useState(project.progress ?? 0)
  const { user } = useAuthStore()
  const canEdit = user && (user.role?.level ?? 99) <= 3

  useEffect(() => { setValue(project.progress ?? 0) }, [project.progress])

  const saveMutation = useMutation({
    mutationFn: (v: number) => projectsApi.update(project.id, { progress: v }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['project', project.id] })
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
    },
    onError: () => {
      // Rollback dell'optimistic al valore salvato sul progetto.
      setValue(project.progress ?? 0)
    },
  })
  const saving = saveMutation.isPending
  const save = (v: number) => saveMutation.mutate(v)

  const barColor = value >= 100 ? 'bg-success-500' : value >= 50 ? 'bg-primary-500' : 'bg-primary-400'

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-medium text-slate-600">Avanzamento manuale</span>
        <span className="text-xs text-slate-500 tabular-nums">{value}%</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden mb-2">
        <div className={cn('h-full rounded-full transition-all', barColor)} style={{ width: `${value}%` }} />
      </div>
      {canEdit && (
        <input
          type="range"
          min={0}
          max={100}
          step={5}
          value={value}
          onChange={(e) => setValue(Number(e.target.value))}
          onMouseUp={() => save(value)}
          onTouchEnd={() => save(value)}
          className="w-full h-1.5 accent-primary-500 cursor-pointer"
          disabled={saving}
        />
      )}
    </div>
  )
}

// ============================================================
// OVERVIEW TAB
// ============================================================

function OverviewTab({ project }: { project: ProjectWithRelations }) {
  const navigate = useNavigate()
  const budget = project.budget ?? 0
  const spentBudget = project.spentBudget ?? 0
  // Time progress: how far between startDate and endDate
  const now = Date.now()
  const start = project.startDate ? new Date(project.startDate).getTime() : now
  const end = project.endDate ? new Date(project.endDate).getTime() : now
  const timeTotal = end - start
  const timeElapsed = now - start

  return (
    <div className="space-y-6">
      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <StatCard
          label="Budget Cliente"
          value={budget ? EUR.format(budget) : '-'}
          icon={DollarSign}
          tone="primary"
        />
        <StatCard
          label="Budget Interno"
          value={spentBudget ? EUR.format(spentBudget) : '-'}
          icon={TrendingUp}
          tone={spentBudget > budget && budget > 0 ? 'danger' : 'success'}
        />
      </div>

      {/* Modulo */}
      {((project.moduleTypes ?? []) as string[]).length > 0 && (
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">Modulo</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {((project.moduleTypes ?? []) as string[]).map((mod: string) => (
              <button
                key={mod}
                onClick={() => navigate(`${MODULE_ROUTE[mod] ?? '/'}?projectId=${project.id}`)}
                className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-card hover:border-slate-300 transition-colors text-left group"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">
                    {MODULE_LABEL[mod as keyof typeof MODULE_LABEL] ?? mod}
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5">Vai al modulo</p>
                </div>
                <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-primary-600 transition-colors flex-shrink-0" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Progress bars */}
      <Card padding="md">
        <CardHeader title="Avanzamento" />
        <div className="space-y-4 mt-3">
          {/* Task progress — fonte primaria di avanzamento */}
          {(() => {
            const totalTasks = project.tasks?.length ?? project._count?.tasks ?? 0
            const doneTasks = project.tasks
              ? project.tasks.filter((t) => t.status === 'DONE').length
              : (project._count?.tasksDone ?? 0)
            return totalTasks > 0 ? (
              <ProgressBar label={`Task (${doneTasks}/${totalTasks})`} value={doneTasks} max={totalTasks} color="success" />
            ) : null
          })()}
          {/* Avanzamento manuale — visibile quando non ci sono task */}
          {(() => {
            const totalTasks = project.tasks?.length ?? project._count?.tasks ?? 0
            if (totalTasks > 0) return null
            return <ManualProgressSlider project={project} />
          })()}
          {budget > 0 && (
            <ProgressBar label="Budget" value={spentBudget} max={budget} warnOnHigh />
          )}
          {timeTotal > 0 && project.startDate && project.endDate && (
            <ProgressBar label="Timeline" value={Math.max(0, timeElapsed)} max={timeTotal} color="accent" />
          )}
        </div>
      </Card>

      {/* Task status summary */}
      {project.tasks && project.tasks.length > 0 && (
        <Card padding="md">
          <CardHeader title="Riepilogo Task" />
          <div className="flex items-center gap-2 flex-wrap mt-3">
            {(Object.keys(TASK_STATUS_LABEL) as TaskStatus[]).map((status) => {
              const count = project.tasks!.filter((t) => t.status === status).length
              if (count === 0) return null
              return (
                <span key={status} className={cn('px-2.5 py-1 text-xs font-medium rounded-full', TASK_STATUS_COLORS[status])}>
                  {TASK_STATUS_LABEL[status]}: {count}
                </span>
              )
            })}
          </div>
        </Card>
      )}

      {/* Description */}
      {project.description && (
        <Card padding="md">
          <CardHeader title="Descrizione" />
          <p className="text-sm text-slate-600 leading-relaxed mt-2 whitespace-pre-wrap">{project.description}</p>
        </Card>
      )}

      {/* Documenti Progetto */}
      <ProjectDocuments projectId={project.id} />

      {/* Aggiornamenti/Commenti */}
      <ProjectComments projectId={project.id} />

      {/* Notes */}
      <ProjectNotes projectId={project.id} />
    </div>
  )
}

// ============================================================
// PROJECT PROPOSAL
// ============================================================

function ProjectDocuments({ projectId }: { projectId: string }) {
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const { user } = useAuthStore()
  const canEdit = user && (user.role?.level ?? 99) <= 3

  const { data: documents = [], isLoading } = useQuery({
    queryKey: ['project-documents', projectId],
    queryFn: async () => {
      const res = await projectsApi.listDocuments(projectId)
      return (res.data as any)?.data ?? res.data ?? []
    },
  })

  const uploadMutation = useMutation({
    mutationFn: (file: File) => projectsApi.uploadDocument(projectId, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-documents', projectId] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (docId: string) => projectsApi.deleteDocument(projectId, docId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-documents', projectId] })
    },
  })

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files) {
      Array.from(files).forEach((f) => uploadMutation.mutate(f))
    }
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false)
    const files = e.dataTransfer.files
    if (files) {
      Array.from(files).forEach((f) => uploadMutation.mutate(f))
    }
  }

  const getFileIcon = (mime: string) => {
    if (mime.includes('pdf')) return { bg: 'bg-danger-50', color: 'text-danger-600' }
    if (mime.includes('word') || mime.includes('doc')) return { bg: 'bg-primary-50', color: 'text-primary-600' }
    if (mime.includes('presentation') || mime.includes('ppt')) return { bg: 'bg-warning-50', color: 'text-warning-600' }
    if (mime.includes('spreadsheet') || mime.includes('xls') || mime.includes('excel')) return { bg: 'bg-success-50', color: 'text-success-600' }
    if (mime.includes('image')) return { bg: 'bg-primary-50', color: 'text-primary-600' }
    return { bg: 'bg-slate-100', color: 'text-slate-600' }
  }

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  return (
    <Card padding="md">
      <CardHeader title={`Documenti${documents.length ? ` (${documents.length})` : ''}`} />
      <div className="mt-3 space-y-2">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <Loader2 className="w-4 h-4 animate-spin" /> Caricamento...
          </div>
        ) : (
          <>
            {/* File list */}
            {(documents as any[]).map((doc: any) => {
              const icon = getFileIcon(doc.mimeType || '')
              return (
                <div key={doc.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200 group">
                  <div className={`w-9 h-9 ${icon.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <FileText className={`w-4 h-4 ${icon.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{doc.name}</p>
                    <p className="text-[11px] text-slate-400">
                      {formatSize(doc.size || 0)}
                      {doc.uploadedBy && ` · ${doc.uploadedBy.firstName} ${doc.uploadedBy.lastName}`}
                      {doc.createdAt && ` · ${new Date(doc.createdAt).toLocaleDateString('it-IT')}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    {doc.presignedUrl && (
                      <>
                        <a href={doc.presignedUrl} target="_blank" rel="noopener noreferrer"
                          className="p-1.5 text-slate-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors" title="Visualizza">
                          <Eye className="w-3.5 h-3.5" />
                        </a>
                        <a href={doc.presignedUrl} download={doc.name}
                          className="p-1.5 text-slate-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors" title="Scarica">
                          <Download className="w-3.5 h-3.5" />
                        </a>
                      </>
                    )}
                    {canEdit && (
                      <button
                        onClick={async () => {
                          if (await confirm({ title: 'Eliminare documento?', message: `"${doc.name}" verrà eliminato definitivamente.`, danger: true })) {
                            deleteMutation.mutate(doc.id)
                          }
                        }}
                        disabled={deleteMutation.isPending}
                        className="p-1.5 text-slate-400 hover:text-danger-600 hover:bg-danger-50 rounded-lg transition-colors" title="Elimina">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              )
            })}

            {/* Drop zone / upload */}
            {canEdit && (
              <div
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true) }}
                onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true) }}
                onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false) }}
                onDrop={handleDrop}
                className={`w-full flex flex-col items-center justify-center gap-1.5 p-5 border-2 border-dashed rounded-lg text-sm cursor-pointer transition-colors ${
                  isDragging
                    ? 'border-primary-500 bg-primary-50 text-primary-600'
                    : 'border-slate-300 text-slate-500 hover:border-primary-400 hover:text-primary-600 hover:bg-primary-50/50'
                } ${uploadMutation.isPending ? 'pointer-events-none opacity-60' : ''}`}
              >
                {uploadMutation.isPending ? (
                  <><Loader2 className="w-5 h-5 animate-spin" /> Caricamento...</>
                ) : isDragging ? (
                  <><Download className="w-5 h-5" /> Rilascia i file qui</>
                ) : (
                  <>
                    <Upload className="w-5 h-5" />
                    <span>Trascina qui i file oppure clicca per selezionarli</span>
                    <span className="text-xs text-slate-400">PDF, DOC, PPT, XLS, immagini (max 50MB)</span>
                  </>
                )}
              </div>
            )}

            {!canEdit && documents.length === 0 && (
              <p className="text-sm text-slate-400">Nessun documento caricato</p>
            )}
          </>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.jpg,.jpeg,.png"
          onChange={handleFileChange}
          className="hidden"
          multiple
        />
      </div>
    </Card>
  )
}

// ============================================================
// PROJECT COMMENTS (Aggiornamenti con @menzioni)
// ============================================================

interface ProjectComment {
  id: string
  text: string
  mentions: string[]
  createdAt: string
  user: { id: string; firstName: string; lastName: string; avatar?: string | null }
}

function ProjectComments({ projectId }: { projectId: string }) {
  const queryClient = useQueryClient()
  const user = useAuthStore((s) => s.user)
  const [text, setText] = useState('')
  const [mentions, setMentions] = useState<string[]>([])
  const [showMentionDropdown, setShowMentionDropdown] = useState(false)
  const [mentionFilter, setMentionFilter] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Fetch comments
  const { data: commentsRaw, isLoading } = useQuery({
    queryKey: ['project-comments', projectId],
    queryFn: async () => {
      const res = await projectsApi.getComments(projectId)
      return (res as any).data ?? res
    },
    staleTime: 30_000,
  })
  const comments: ProjectComment[] = Array.isArray(commentsRaw) ? commentsRaw : (commentsRaw as any)?.data ?? []

  // Fetch all users for @mentions (team potrebbe essere vuoto)
  const { data: allUsersRaw } = useQuery({
    queryKey: ['users-all-mentions'],
    queryFn: async () => {
      const res = await usersApi.list({ limit: 200 })
      const r = res as any
      return r?.users ?? r?.data?.users ?? r?.items ?? r ?? []
    },
    staleTime: 5 * 60_000,
  })
  const teamMembers: Array<{ id: string; firstName: string; lastName: string }> = (() => {
    const list = allUsersRaw as any
    if (!Array.isArray(list)) return []
    return list.filter((u: any) => u?.id && u.id !== user?.id)
  })()

  const addMutation = useMutation({
    mutationFn: () => projectsApi.addComment(projectId, text.trim(), mentions),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-comments', projectId] })
      setText('')
      setMentions([])
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (commentId: string) => projectsApi.deleteComment(projectId, commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-comments', projectId] })
    },
  })

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      if (text.trim()) addMutation.mutate()
    }
  }

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value
    setText(val)

    // Detect @ for mention — supporta lettere accentate e spazi nel nome
    const cursorPos = e.target.selectionStart
    const textBefore = val.substring(0, cursorPos)
    // Cerca l'ultima @ preceduta da inizio stringa o spazio/newline, seguita da lettere opzionali
    const atMatch = textBefore.match(/(?:^|[\s\n])@([a-zA-ZÀ-ÿ]*)$/)
    // Oppure @ è l'ultimo carattere
    const atEnd = textBefore.endsWith('@')
    if (atMatch || atEnd) {
      setShowMentionDropdown(true)
      setMentionFilter((atMatch?.[1] ?? '').toLowerCase())
    } else {
      setShowMentionDropdown(false)
    }
  }

  const insertMention = (member: { id: string; firstName: string; lastName: string }) => {
    const cursorPos = textareaRef.current?.selectionStart ?? text.length
    const textBefore = text.substring(0, cursorPos)
    const textAfter = text.substring(cursorPos)
    const atIndex = textBefore.lastIndexOf('@')
    const newText = textBefore.substring(0, atIndex) + `@${member.firstName} ${member.lastName} ` + textAfter
    setText(newText)
    if (!mentions.includes(member.id)) {
      setMentions([...mentions, member.id])
    }
    setShowMentionDropdown(false)
    textareaRef.current?.focus()
  }

  const filteredMembers = teamMembers.filter(m =>
    `${m.firstName} ${m.lastName}`.toLowerCase().includes(mentionFilter)
  )

  const getInitials = (u: { firstName: string; lastName: string }) =>
    `${u.firstName?.[0] ?? ''}${u.lastName?.[0] ?? ''}`.toUpperCase()

  const formatTime = (d: string) => {
    const date = new Date(d)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)
    if (diffMins < 1) return 'Adesso'
    if (diffMins < 60) return `${diffMins} min fa`
    if (diffHours < 24) return `${diffHours}h fa`
    if (diffDays < 7) return `${diffDays}g fa`
    return date.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })
  }

  // Evidenzia @menzioni nel testo
  const renderText = (t: string) => {
    const parts = t.split(/(@\w+\s\w+)/g)
    return parts.map((part, i) =>
      part.startsWith('@') ? (
        <span key={i} className="text-primary-600 font-medium">{part}</span>
      ) : (
        <span key={i}>{part}</span>
      )
    )
  }

  return (
    <Card padding="md">
      <CardHeader title="Aggiornamenti" />
      <div className="mt-3 space-y-3">
        {/* Input */}
        <div className="flex gap-3">
          <div className="w-8 h-8 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center text-xs font-semibold flex-shrink-0 mt-1">
            {user ? getInitials(user) : '?'}
          </div>
          <div className="flex-1 relative">
            <textarea
              ref={textareaRef}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg resize-none focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 placeholder:text-slate-400"
              rows={2}
              placeholder="Scrivi un aggiornamento... usa @ per menzionare"
              value={text}
              onChange={handleTextChange}
              onKeyDown={handleKeyDown}
            />
            {/* @Mention dropdown */}
            {showMentionDropdown && filteredMembers.length > 0 && (
              <div className="absolute left-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-dropdown z-10 max-h-40 overflow-y-auto w-56">
                {filteredMembers.map(m => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => insertMention(m)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 transition-colors flex items-center gap-2"
                  >
                    <AtSign className="w-3 h-3 text-primary-500" />
                    {m.firstName} {m.lastName}
                  </button>
                ))}
              </div>
            )}
            <div className="flex items-center justify-between mt-1">
              <p className="text-[10px] text-slate-400">&#8984;+Enter per inviare &middot; @ per menzionare</p>
              <button
                onClick={() => { if (text.trim()) addMutation.mutate() }}
                disabled={!text.trim() || addMutation.isPending}
                className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
              >
                <Send className="w-3 h-3" />
                {addMutation.isPending ? '...' : 'Invia'}
              </button>
            </div>
          </div>
        </div>

        {/* Comments list */}
        {isLoading && (
          <div className="flex justify-center py-4">
            <Loader2 className="w-5 h-5 animate-spin text-slate-300" />
          </div>
        )}

        {!isLoading && comments.length === 0 && (
          <div className="text-center py-6">
            <MessageSquare className="w-8 h-8 text-slate-200 mx-auto mb-2" />
            <p className="text-xs text-slate-400">Nessun aggiornamento. Scrivi il primo!</p>
          </div>
        )}

        {comments.map((comment) => (
          <div key={comment.id} className="flex gap-3 group">
            <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center text-xs font-semibold flex-shrink-0">
              {getInitials(comment.user)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-800">
                  {comment.user.firstName} {comment.user.lastName}
                </span>
                <span className="text-[11px] text-slate-400">{formatTime(comment.createdAt)}</span>
                {(comment.user.id === user?.id || (user?.role?.level ?? 5) <= 2) && (
                  <button
                    onClick={async () => {
                      if (await confirm({ title: 'Eliminare aggiornamento?', danger: true })) {
                        deleteMutation.mutate(comment.id)
                      }
                    }}
                    className="p-0.5 text-slate-300 hover:text-danger-500 transition-all sm:opacity-0 sm:group-hover:opacity-100"
                    title="Elimina"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap mt-0.5">
                {renderText(comment.text)}
              </p>
            </div>
          </div>
        ))}
      </div>
    </Card>
  )
}

// ============================================================
// PROJECT NOTES
// ============================================================

interface ProjectNote {
  id: string
  text: string
  createdAt: string
  user: { id: string; firstName: string; lastName: string; avatar?: string | null }
}

function ProjectNotes({ projectId }: { projectId: string }) {
  const queryClient = useQueryClient()
  const user = useAuthStore((s) => s.user)

  const { data: notesRaw, isLoading } = useQuery({
    queryKey: ['project-notes', projectId],
    queryFn: async () => {
      const res = await projectsApi.getNotes(projectId)
      return (res as any).data ?? res
    },
    staleTime: 30_000,
  })
  const notes: ProjectNote[] = Array.isArray(notesRaw) ? notesRaw : (notesRaw as any)?.data ?? []

  const addMutation = useMutation({
    mutationFn: (noteText: string) => projectsApi.addNote(projectId, noteText),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-notes', projectId] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (noteId: string) => projectsApi.deleteNote(projectId, noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-notes', projectId] })
    },
  })

  const getInitials = (u: { firstName: string; lastName: string }) =>
    `${u.firstName?.[0] ?? ''}${u.lastName?.[0] ?? ''}`.toUpperCase()

  const formatTime = (d: string) => {
    const date = new Date(d)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Adesso'
    if (diffMins < 60) return `${diffMins} min fa`
    if (diffHours < 24) return `${diffHours}h fa`
    if (diffDays < 7) return `${diffDays}g fa`
    return date.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined })
  }

  return (
    <Card padding="md">
      <CardHeader title="Note" />
      <div className="mt-3 space-y-3">
        {/* Rich Editor */}
        <div className="flex gap-3">
          <div className="w-8 h-8 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center text-xs font-semibold flex-shrink-0 mt-1">
            {user ? getInitials(user) : '?'}
          </div>
          <div className="flex-1">
            <RichNoteEditor
              onSubmit={(html) => addMutation.mutate(html)}
              isPending={addMutation.isPending}
              placeholder="Scrivi una nota..."
            />
            <p className="text-[10px] text-slate-400 mt-1">⌘+Enter per pubblicare</p>
          </div>
        </div>

        {/* Notes list */}
        {isLoading && (
          <div className="flex justify-center py-4">
            <Loader2 className="w-5 h-5 animate-spin text-slate-300" />
          </div>
        )}

        {!isLoading && notes.length === 0 && (
          <div className="text-center py-6">
            <MessageSquare className="w-8 h-8 text-slate-200 mx-auto mb-2" />
            <p className="text-xs text-slate-400">Nessuna nota ancora. Aggiungi la prima!</p>
          </div>
        )}

        {notes.map((note) => (
          <div key={note.id} className="flex gap-3 group">
            <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center text-xs font-semibold flex-shrink-0">
              {getInitials(note.user)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-800">
                  {note.user.firstName} {note.user.lastName}
                </span>
                <span className="text-[11px] text-slate-400">{formatTime(note.createdAt)}</span>
                {(note.user.id === user?.id || (user?.role?.level ?? 5) <= 2) && (
                  <button
                    onClick={async () => {
                      if (await confirm({ title: 'Eliminare nota?', danger: true })) {
                        deleteMutation.mutate(note.id)
                      }
                    }}
                    className="p-0.5 text-slate-300 hover:text-danger-500 transition-all sm:opacity-0 sm:group-hover:opacity-100"
                    title="Elimina nota"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              {note.text.startsWith('<') ? (
                <div className="text-sm text-slate-600 leading-relaxed mt-0.5 note-html" dangerouslySetInnerHTML={{ __html: note.text }} />
              ) : (
                <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap mt-0.5">{note.text}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </Card>
  )
}

// ============================================================
// TEAM TAB
// ============================================================

const MODULE_ROLES: Record<string, string[]> = {
  SOCIAL: ['Social Media Manager', 'Community Manager', 'Content Creator', 'Copywriter', 'Graphic Designer', 'Video Maker', 'Photographer'],
  ADV: ['Campaign Manager', 'Media Buyer', 'Performance Specialist', 'Data Analyst', 'Copywriter', 'Graphic Designer'],
  DIGITAL: ['Project Manager', 'Web Developer', 'UX/UI Designer', 'SEO Specialist', 'Backend Developer', 'Frontend Developer', 'DevOps'],
  PR: ['PR Manager', 'Press Officer', 'Media Relations Specialist', 'Copywriter', 'Crisis Manager'],
  BRANDING: ['Art Director', 'Graphic Designer', 'Brand Strategist', 'Copywriter', 'Illustrator'],
  CONSULENZA: ['Senior Consultant', 'Strategy Analyst', 'Marketing Specialist', 'Data Analyst'],
  EVENTI: ['Event Manager', 'Logistic Coordinator', 'Hostess/Steward', 'Tecnico Audio/Video', 'Catering Manager', 'Graphic Designer'],
}

function TeamTab({ project }: { project: ProjectWithRelations }) {
  const queryClient = useQueryClient()
  const [showAddForm, setShowAddForm] = useState(false)
  const [newMemberUserId, setNewMemberUserId] = useState('')
  const [newMemberRole, setNewMemberRole] = useState('')
  const [customRole, setCustomRole] = useState(false)

  const { data: usersData } = useQuery({
    queryKey: ['users', { limit: 100, forDropdown: 'managers' }],
    queryFn: () => usersApi.list({ limit: 100, forDropdown: 'managers' } as any),
    enabled: showAddForm,
  })
  const users: User[] = (() => {
    const raw = usersData as any
    const inner = raw?.data ?? raw
    if (Array.isArray(inner)) return inner
    if (inner?.users && Array.isArray(inner.users)) return inner.users
    if (inner?.items && Array.isArray(inner.items)) return inner.items
    return []
  })()

  const addMutation = useMutation({
    mutationFn: (data: { userId: string; role: string }) =>
      projectsApi.addTeamMember(project.id, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['project', project.id] })
      setShowAddForm(false)
      setNewMemberUserId('')
      setNewMemberRole('')
    },
  })

  const removeMutation = useMutation({
    mutationFn: (userId: string) =>
      projectsApi.removeTeamMember(project.id, userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['project', project.id] })
    },
  })

  const team = project.team ?? []
  const existingUserIds = new Set(team.map((m) => m.userId))

  return (
    <div className="space-y-3">
      {/* Add button */}
      {!showAddForm && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setShowAddForm(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
          >
            <UserPlus className="w-4 h-4" />
            Aggiungi Membro
          </button>
        </div>
      )}

      {/* Add form */}
      {showAddForm && (
        <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Utente</label>
              <select
                className={inputClass}
                value={newMemberUserId}
                onChange={(e) => setNewMemberUserId(e.target.value)}
              >
                <option value="">Seleziona utente...</option>
                {users
                  .filter((u) => !existingUserIds.has(u.id))
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.firstName} {u.lastName}
                    </option>
                  ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Ruolo nel progetto</label>
              {!customRole ? (
                <select
                  className={inputClass}
                  value={newMemberRole}
                  onChange={(e) => {
                    if (e.target.value === '__custom__') {
                      setCustomRole(true)
                      setNewMemberRole('')
                    } else {
                      setNewMemberRole(e.target.value)
                    }
                  }}
                >
                  <option value="">Seleziona ruolo...</option>
                  {(() => {
                    const mod = ((project.moduleTypes ?? []) as string[])[0] ?? ''
                    const roles = MODULE_ROLES[mod] ?? []
                    return roles.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))
                  })()}
                  <option value="__custom__">Altro (scrivi a mano)</option>
                </select>
              ) : (
                <div className="flex gap-1.5">
                  <input
                    type="text"
                    className={cn(inputClass, 'flex-1')}
                    value={newMemberRole}
                    onChange={(e) => setNewMemberRole(e.target.value)}
                    placeholder="Scrivi il ruolo..."
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => { setCustomRole(false); setNewMemberRole('') }}
                    className="px-2 text-xs text-slate-500 hover:text-slate-700 border border-slate-200 rounded-md hover:bg-slate-50 transition-colors"
                  >
                    Lista
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => addMutation.mutate({ userId: newMemberUserId, role: newMemberRole || 'Membro' })}
              disabled={!newMemberUserId || !newMemberRole || addMutation.isPending}
              className="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              {addMutation.isPending ? 'Aggiunta...' : 'Aggiungi'}
            </button>
            <button
              type="button"
              onClick={() => { setShowAddForm(false); setNewMemberUserId(''); setNewMemberRole(''); setCustomRole(false) }}
              className="px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
            >
              Annulla
            </button>
          </div>
        </div>
      )}

      {addMutation.isError && <ErrorBanner message={getApiErrorMessage(addMutation.error)} />}
      {removeMutation.isError && <ErrorBanner message={getApiErrorMessage(removeMutation.error)} />}

      {/* Empty state */}
      {team.length === 0 && !showAddForm && (
        <div className="text-center py-12 text-slate-400">
          <Users className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p className="text-sm">Nessun membro nel team</p>
        </div>
      )}

      {/* Team list */}
      {team.map((member) => (
        <div key={member.id} className="flex items-center gap-4 p-4 bg-white rounded-2xl border border-slate-200 shadow-card">
          {/* Avatar */}
          <div className="w-9 h-9 bg-primary-50 rounded-full flex items-center justify-center flex-shrink-0">
            {member.user.avatar ? (
              <img src={member.user.avatar} alt="" className="w-9 h-9 rounded-full object-cover" />
            ) : (
              <span className="text-sm font-semibold text-primary-600">
                {member.user.firstName.charAt(0).toUpperCase()}
              </span>
            )}
          </div>
          {/* Info */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900">
              {member.user.firstName} {member.user.lastName}
            </p>
            <p className="text-xs text-slate-400">{member.role}</p>
          </div>
          {/* System role badge */}
          {member.user.role?.name && (
            <Badge variant="neutral" size="sm">{member.user.role.name}</Badge>
          )}
          {/* Remove */}
          <button
            type="button"
            onClick={async () => {
              if (await confirm({ title: 'Rimuovere membro?', message: `${member.user.firstName} ${member.user.lastName} verrà rimosso dal team.`, danger: true })) {
                removeMutation.mutate(member.userId)
              }
            }}
            disabled={removeMutation.isPending}
            className="p-1.5 text-slate-400 hover:text-danger-600 transition-colors disabled:opacity-50"
            title="Rimuovi dal team"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
  )
}

// ============================================================
// TASKS TAB
// ============================================================

function TaskStatusDropdown({ task, onStatusChange, isPending }: {
  task: ProjectTask
  onStatusChange: (taskId: string, status: TaskStatus) => void
  isPending: boolean
}) {
  const [open, setOpen] = useState(false)

  return (
    <div className="relative">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(!open) }}
        disabled={isPending}
        className={cn(
          'flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0 transition-colors',
          TASK_STATUS_COLORS[task.status],
          'hover:ring-2 hover:ring-slate-300 disabled:opacity-50'
        )}
      >
        {TASK_STATUS_LABEL[task.status]}
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-slate-200 rounded-lg shadow-dropdown py-1 min-w-[140px]">
            {(Object.keys(TASK_STATUS_LABEL) as TaskStatus[])
              .filter((s) => s !== task.status && s !== 'CANCELLED')
              .map((status) => (
                <button
                  key={status}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    onStatusChange(task.id, status)
                    setOpen(false)
                  }}
                  className="w-full text-left px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2"
                >
                  <span className={cn('w-2 h-2 rounded-full', TASK_STATUS_COLORS[status]?.split(' ')[0] ?? 'bg-slate-300')} />
                  {TASK_STATUS_LABEL[status]}
                </button>
              ))}
          </div>
        </>
      )}
    </div>
  )
}

function TasksTab({ project }: { project: ProjectWithRelations }) {
  const queryClient = useQueryClient()
  const currentUser = useAuthStore((state) => state.user)
  const userLevel = currentUser?.role?.level ?? 5
  const tasks = project.tasks ?? []
  const [showAddForm, setShowAddForm] = useState(false)
  const [newTaskTitle, setNewTaskTitle] = useState('')
  const [newTaskPriority, setNewTaskPriority] = useState<TaskPriority>('MEDIUM')
  const [newTaskAssignTarget, setNewTaskAssignTarget] = useState('') // departmentId or userId
  const [newTaskAssignMode, setNewTaskAssignMode] = useState<'department' | 'person'>('person') // default: persona
  const [newTaskDueDate, setNewTaskDueDate] = useState('')

  // Admin/PM (level <= 2): carica lista reparti
  const { data: departmentsData } = useQuery({
    queryKey: ['departments'],
    queryFn: () => departmentsApi.list(),
    enabled: showAddForm && userLevel <= 2,
  })
  const departments: Array<{ id: string; name: string; code: string }> = (() => {
    const raw = departmentsData as any
    if (Array.isArray(raw)) return raw
    if (raw?.departments && Array.isArray(raw.departments)) return raw.departments
    if (raw?.data && Array.isArray(raw.data)) return raw.data
    return []
  })()

  // Carica utenti per assegnazione diretta (Admin + Manager)
  const { data: usersData } = useQuery({
    queryKey: ['users', { limit: 100 }],
    queryFn: () => usersApi.list({ limit: 100 }),
    enabled: showAddForm && userLevel <= 3,
  })
  const allUsersList: User[] = (() => {
    const raw = usersData as any
    const inner = raw?.data ?? raw
    let allUsers: User[] = []
    if (Array.isArray(inner)) allUsers = inner
    else if (inner?.users && Array.isArray(inner.users)) allUsers = inner.users
    else if (inner?.items && Array.isArray(inner.items)) allUsers = inner.items
    // Manager vede solo il suo reparto (+ se stesso)
    if (userLevel === 3 && currentUser) {
      return allUsers.filter((u) => u.id === currentUser.id || u.departmentId === currentUser.departmentId)
    }
    return allUsers
  })()

  const statusMutation = useMutation({
    mutationFn: ({ taskId, status }: { taskId: string; status: TaskStatus }) =>
      apiClient.patch(`/tasks/${taskId}/status`, { status }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['project', project.id] })
      void queryClient.invalidateQueries({ queryKey: ['tasks'] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (taskId: string) => apiClient.delete(`/tasks/${taskId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['project', project.id] })
      void queryClient.invalidateQueries({ queryKey: ['tasks'] })
      void queryClient.invalidateQueries({ queryKey: ['team-workload'] })
    },
  })

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.post('/tasks', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['project', project.id] })
      void queryClient.invalidateQueries({ queryKey: ['tasks'] })
      void queryClient.invalidateQueries({ queryKey: ['team-workload'] })
      setShowAddForm(false)
      setNewTaskTitle('')
      setNewTaskPriority('MEDIUM')
      setNewTaskAssignTarget('')
      setNewTaskDueDate('')
    },
  })

  function handleStatusChange(taskId: string, newStatus: TaskStatus) {
    statusMutation.mutate({ taskId, status: newStatus })
  }

  function handleCreateTask() {
    const payload: Record<string, unknown> = {
      title: newTaskTitle,
      projectId: project.id,
      clientId: project.clientId,
      priority: newTaskPriority,
      dueDate: newTaskDueDate || undefined,
    }

    if (newTaskAssignTarget) {
      if (userLevel <= 2 && newTaskAssignMode === 'department') {
        // Admin con modalita' reparto
        payload.departmentId = newTaskAssignTarget
      } else {
        // Assegnazione diretta a persona
        payload.assigneeId = newTaskAssignTarget
      }
    }

    createMutation.mutate(payload)
  }

  // Count by status
  const statusCounts = tasks.reduce<Record<string, number>>((acc, t) => {
    acc[t.status] = (acc[t.status] ?? 0) + 1
    return acc
  }, {})

  return (
    <div className="space-y-4">
      {/* Header with add button */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-500">
          {tasks.length} {tasks.length === 1 ? 'task' : 'task'} totali
        </div>
        {!showAddForm && (
          <button
            type="button"
            onClick={() => setShowAddForm(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Aggiungi Task
          </button>
        )}
      </div>

      {/* Add task form */}
      {showAddForm && (
        <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
          <h3 className="text-sm font-semibold text-slate-800">Nuovo Task</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-slate-700 mb-1">Titolo *</label>
              <input
                type="text"
                className={inputClass}
                value={newTaskTitle}
                onChange={(e) => setNewTaskTitle(e.target.value)}
                placeholder="es. Creazione landing page..."
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Priorita'</label>
              <select
                className={inputClass}
                value={newTaskPriority}
                onChange={(e) => setNewTaskPriority(e.target.value as TaskPriority)}
              >
                <option value="LOW">Bassa</option>
                <option value="MEDIUM">Media</option>
                <option value="HIGH">Alta</option>
                <option value="URGENT">Urgente</option>
              </select>
            </div>
            <div>
              {userLevel <= 2 ? (
                <>
                  <div className="flex items-center gap-2 mb-1">
                    <label className="block text-xs font-medium text-slate-700">Assegna</label>
                    <div className="flex bg-slate-100 rounded-md p-0.5 text-[10px] font-medium">
                      <button
                        type="button"
                        onClick={() => { setNewTaskAssignMode('person'); setNewTaskAssignTarget('') }}
                        className={`px-2 py-0.5 rounded transition-colors ${newTaskAssignMode === 'person' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500'}`}
                      >
                        Persona
                      </button>
                      <button
                        type="button"
                        onClick={() => { setNewTaskAssignMode('department'); setNewTaskAssignTarget('') }}
                        className={`px-2 py-0.5 rounded transition-colors ${newTaskAssignMode === 'department' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500'}`}
                      >
                        Reparto
                      </button>
                    </div>
                  </div>
                  <select
                    className={inputClass}
                    value={newTaskAssignTarget}
                    onChange={(e) => setNewTaskAssignTarget(e.target.value)}
                  >
                    <option value="">Non assegnato</option>
                    {newTaskAssignMode === 'department'
                      ? departments.map((d) => (
                          <option key={d.id} value={d.id}>{d.name}</option>
                        ))
                      : allUsersList.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.firstName} {u.lastName}{u.id === currentUser?.id ? ' (me)' : ''}
                          </option>
                        ))}
                  </select>
                </>
              ) : (
                <>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Assegna a</label>
                  <select
                    className={inputClass}
                    value={newTaskAssignTarget}
                    onChange={(e) => setNewTaskAssignTarget(e.target.value)}
                  >
                    <option value="">Non assegnato</option>
                    {allUsersList.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.firstName} {u.lastName}{u.id === currentUser?.id ? ' (me)' : ''}
                      </option>
                    ))}
                  </select>
                </>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Scadenza</label>
              <input
                type="date"
                className={inputClass}
                value={newTaskDueDate}
                onChange={(e) => setNewTaskDueDate(e.target.value)}
              />
            </div>
          </div>
          {userLevel <= 2 && newTaskAssignMode === 'department' && newTaskAssignTarget && (
            <p className="text-xs text-slate-400">
              Il capo reparto ricevera' la task nella sua coda e la assegnera' alla persona giusta.
            </p>
          )}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleCreateTask}
              disabled={!newTaskTitle.trim() || createMutation.isPending}
              className="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              {createMutation.isPending ? 'Creazione...' : 'Crea Task'}
            </button>
            <button
              type="button"
              onClick={() => { setShowAddForm(false); setNewTaskTitle(''); setNewTaskAssignTarget(''); setNewTaskAssignMode('person'); setNewTaskDueDate('') }}
              className="px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
            >
              Annulla
            </button>
          </div>
        </div>
      )}

      {createMutation.isError && <ErrorBanner message={getApiErrorMessage(createMutation.error)} />}
      {statusMutation.isError && (
        <ErrorBanner message={getApiErrorMessage(statusMutation.error)} />
      )}

      {/* Status summary */}
      {tasks.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          {(Object.keys(TASK_STATUS_LABEL) as TaskStatus[]).map((status) => {
            const count = statusCounts[status] ?? 0
            if (count === 0) return null
            return (
              <span key={status} className={cn('px-2.5 py-1 text-xs font-medium rounded-full', TASK_STATUS_COLORS[status])}>
                {TASK_STATUS_LABEL[status]}: {count}
              </span>
            )
          })}
        </div>
      )}

      {/* Empty state */}
      {tasks.length === 0 && !showAddForm && (
        <div className="text-center py-12 text-slate-400">
          <ListChecks className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p className="text-sm">Nessuna task associata a questo progetto</p>
        </div>
      )}

      {/* Task list */}
      {tasks.map((task) => (
        <div
          key={task.id}
          className="flex items-center gap-4 p-4 bg-white rounded-2xl border border-slate-200 shadow-card"
        >
          {/* Priority dot */}
          <div className={cn('w-2 h-2 rounded-full flex-shrink-0', {
            'bg-slate-300': task.priority === 'LOW',
            'bg-primary-400': task.priority === 'MEDIUM',
            'bg-warning-500': task.priority === 'HIGH',
            'bg-danger-500': task.priority === 'URGENT',
          })} title={PRIORITY_LABEL[task.priority]} />

          {/* Info */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">{task.title}</p>
            <div className="flex items-center gap-2 mt-0.5">
              {task.assignee && (
                <span className="text-xs text-slate-400">
                  {task.assignee.firstName} {task.assignee.lastName}
                </span>
              )}
              {task.dueDate && (
                <span className={cn('text-xs', new Date(task.dueDate) < new Date() && task.status !== 'DONE' ? 'text-danger-600' : 'text-slate-400')}>
                  Scadenza: {new Date(task.dueDate).toLocaleDateString('it-IT')}
                </span>
              )}
            </div>
          </div>

          {/* Status dropdown */}
          <TaskStatusDropdown
            task={task}
            onStatusChange={handleStatusChange}
            isPending={statusMutation.isPending}
          />

          {/* Delete button */}
          <button
            type="button"
            onClick={async () => {
              if (await confirm({ title: 'Eliminare task?', message: `"${task.title}" verrà eliminata definitivamente.`, danger: true })) {
                deleteMutation.mutate(task.id)
              }
            }}
            disabled={deleteMutation.isPending}
            className="p-1.5 text-slate-400 hover:text-danger-600 transition-colors flex-shrink-0"
            title="Elimina task"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}

      {deleteMutation.isError && (
        <ErrorBanner message={getApiErrorMessage(deleteMutation.error)} />
      )}
    </div>
  )
}

// ============================================================
// EXPENSES TAB
// ============================================================

const EXPENSE_CATEGORY_LABEL: Record<ExpenseCategory, string> = {
  FORNITORE: 'Fornitore',
  MEDIA_BUYING: 'Media Buying',
  SOFTWARE: 'Software',
  VIAGGIO: 'Viaggio',
  AUTOMEZZI: 'Automezzi',
  STAMPA: 'Stampa',
  CATERING: 'Catering',
  LOCATION: 'Location',
  PERSONALE: 'Personale',
  ATTREZZATURA: 'Attrezzatura',
  UFFICIO: 'Ufficio',
  BANCARIO: 'Bancario',
  F24: 'F24',
  PRELIEVO: 'Prelievo',
  ALTRO: 'Altro',
}

const EXPENSE_CATEGORIES: ExpenseCategory[] = [
  'FORNITORE', 'MEDIA_BUYING', 'SOFTWARE', 'VIAGGIO', 'AUTOMEZZI', 'STAMPA',
  'CATERING', 'LOCATION', 'PERSONALE', 'BANCARIO', 'ALTRO',
]

function ExpensesTab({ project }: { project: ProjectWithRelations }) {
  const queryClient = useQueryClient()
  const draftStore = useExpenseDraftStore()
  const draft = draftStore.getDraft(project.id)
  const showForm = draft.showForm
  const editingExpense = draft.editing
  const formData = draft.formData
  const setShowForm = (v: boolean) => draftStore.setShowForm(project.id, v)
  const setEditingExpense = (e: Expense | null) => draftStore.setEditing(project.id, e)
  const setFormData = (d: ExpenseFormData) => draftStore.setFormData(project.id, d)

  const { data: expensesData, isLoading } = useQuery({
    queryKey: ['expenses', { projectId: project.id }],
    queryFn: async () => {
      const res = await expensesApi.list({ projectId: project.id, limit: 100 })
      return res.data
    },
  })

  const expenses: Expense[] = (() => {
    const raw = expensesData as any
    if (Array.isArray(raw)) return raw
    if (raw?.items && Array.isArray(raw.items)) return raw.items
    return []
  })()

  // Elenco utenti per il campo "Spesa sostenuta da"
  // NB: usersApi.list usa apiClient.get → ritorna GIÀ il payload scartato ({ users, pagination }),
  // a differenza di expensesApi.list che usa axiosInstance.get (response completa).
  const { data: usersData } = useQuery({
    queryKey: ['users', 'expense-picker'],
    queryFn: async () => {
      const res = await usersApi.list({ limit: 200 })
      return res
    },
    staleTime: 5 * 60 * 1000,
  })
  const users: { id: string; firstName: string; lastName: string }[] = (() => {
    const raw = usersData as any
    const arr = Array.isArray(raw) ? raw : (raw?.users ?? raw?.items ?? raw?.data?.users ?? [])
    return Array.isArray(arr) ? arr : []
  })()

  const totalExpenses = expenses.reduce((sum, e) => sum + Number(e.amount), 0)

  const resetForm = () => {
    draftStore.resetDraft(project.id)
  }

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => expensesApi.create(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['expenses', { projectId: project.id }] })
      void queryClient.invalidateQueries({ queryKey: ['project', project.id] })
      resetForm()
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      expensesApi.update(id, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['expenses', { projectId: project.id }] })
      void queryClient.invalidateQueries({ queryKey: ['project', project.id] })
      resetForm()
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => expensesApi.delete(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['expenses', { projectId: project.id }] })
      void queryClient.invalidateQueries({ queryKey: ['project', project.id] })
    },
  })

  const [receiptFile, setReceiptFile] = useState<File | null>(null)

  const uploadReceiptMutation = useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) =>
      expensesApi.uploadReceipt(id, file),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['expenses', { projectId: project.id }] })
    },
  })

  const deleteReceiptMutation = useMutation({
    mutationFn: (id: string) => expensesApi.deleteReceipt(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['expenses', { projectId: project.id }] })
    },
  })

  const handleViewReceipt = async (expenseId: string) => {
    try {
      const res = await expensesApi.getReceiptUrl(expenseId)
      const url = (res.data as any)?.url ?? (res as any)?.data?.url ?? (res as any)?.url
      if (url) {
        window.open(url, '_blank')
      } else {
        toast.error('File non disponibile')
      }
    } catch {
      toast.error('Impossibile aprire il file')
    }
  }

  const handleSubmit = () => {
    const payload = {
      projectId: project.id,
      description: formData.description,
      amount: formData.amount,
      date: formData.date,
      category: formData.category,
      notes: formData.notes || undefined,
      incurredById: formData.incurredById || null,
    }

    if (editingExpense) {
      updateMutation.mutate({ id: editingExpense.id, data: payload })
    } else {
      createMutation.mutate(payload, {
        onSuccess: (res: any) => {
          // After creating expense, upload receipt if one was selected
          const newExpenseId = res?.data?.id ?? res?.id
          if (receiptFile && newExpenseId) {
            uploadReceiptMutation.mutate({ id: newExpenseId, file: receiptFile })
          }
          setReceiptFile(null)
        },
      })
    }
  }

  const handleEdit = (expense: Expense) => {
    setEditingExpense(expense)
    setFormData({
      description: expense.description,
      amount: String(expense.amount),
      date: expense.date ? (new Date(expense.date).toISOString().split('T')[0] ?? '') : '',
      category: expense.category,
      notes: expense.notes ?? '',
      incurredById: expense.incurredById ?? expense.incurredBy?.id ?? '',
    })
    setShowForm(true)
  }

  const isPending = createMutation.isPending || updateMutation.isPending

  return (
    <div className="space-y-4">
      {/* Header con bottone aggiungi */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-500">
          {expenses.length} {expenses.length === 1 ? 'spesa' : 'spese'} registrate
        </div>
        {!showForm && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Aggiungi Spesa
          </button>
        )}
      </div>

      {/* Form aggiunta/modifica */}
      {showForm && (
        <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
          <h3 className="text-sm font-semibold text-slate-800">
            {editingExpense ? 'Modifica Spesa' : 'Nuova Spesa'}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-slate-700 mb-1">Descrizione *</label>
              <input
                type="text"
                className={inputClass}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="es. Acquisto licenza software..."
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Importo (EUR) *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                className={inputClass}
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Data *</label>
              <input
                type="date"
                className={inputClass}
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Categoria *</label>
              <select
                className={inputClass}
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value as ExpenseCategory })}
              >
                {EXPENSE_CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>{EXPENSE_CATEGORY_LABEL[cat]}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Spesa sostenuta da</label>
              <select
                className={inputClass}
                value={formData.incurredById}
                onChange={(e) => setFormData({ ...formData, incurredById: e.target.value })}
              >
                <option value="">— Seleziona persona —</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Note</label>
              <input
                type="text"
                className={inputClass}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Note aggiuntive..."
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Scontrino / Ricevuta (opzionale)
              </label>
              {editingExpense?.receiptUrl ? (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleViewReceipt(editingExpense.id)}
                    className="flex items-center gap-1.5 text-xs text-primary-600 hover:text-primary-700 transition-colors"
                  >
                    <Paperclip className="w-3.5 h-3.5" />
                    {editingExpense.receiptFilename ?? 'Visualizza file'}
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      if (await confirm({ title: 'Rimuovere allegato?', message: 'Il file allegato verrà rimosso dalla spesa.', danger: true })) {
                        deleteReceiptMutation.mutate(editingExpense.id)
                      }
                    }}
                    disabled={deleteReceiptMutation.isPending}
                    className="text-xs text-danger-500 hover:text-danger-700 transition-colors disabled:opacity-50"
                  >
                    Rimuovi
                  </button>
                  <span className="text-xs text-slate-400">oppure carica un nuovo file:</span>
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) => {
                      const f = e.target.files?.[0]
                      if (f) {
                        uploadReceiptMutation.mutate({ id: editingExpense.id, file: f })
                      }
                    }}
                    className="text-xs text-slate-500 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
                  />
                </div>
              ) : editingExpense ? (
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => {
                    const f = e.target.files?.[0]
                    if (f) {
                      uploadReceiptMutation.mutate({ id: editingExpense.id, file: f })
                    }
                  }}
                  className="text-xs text-slate-500 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
                />
              ) : (
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) => setReceiptFile(e.target.files?.[0] ?? null)}
                    className="text-xs text-slate-500 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
                  />
                  {receiptFile && (
                    <span className="text-xs text-slate-500 flex items-center gap-1">
                      <Upload className="w-3 h-3" />
                      {receiptFile.name}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!formData.description || !formData.amount || !formData.date || isPending}
              className="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              {isPending ? 'Salvataggio...' : editingExpense ? 'Salva Modifiche' : 'Aggiungi'}
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
            >
              Annulla
            </button>
          </div>
        </div>
      )}

      {(createMutation.isError || updateMutation.isError) && (
        <ErrorBanner message={getApiErrorMessage(createMutation.error ?? updateMutation.error)} />
      )}
      {deleteMutation.isError && (
        <ErrorBanner message={getApiErrorMessage(deleteMutation.error)} />
      )}

      {/* Loading */}
      {isLoading && <TabSkeleton rows={3} />}

      {/* Empty state */}
      {!isLoading && expenses.length === 0 && !showForm && (
        <div className="text-center py-12 text-slate-400">
          <Receipt className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p className="text-sm">Nessuna spesa registrata per questo progetto</p>
        </div>
      )}

      {/* Lista spese */}
      {!isLoading && expenses.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-card overflow-x-auto">
          <table className="w-full text-sm min-w-[600px]">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="text-left px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-slate-500">Data</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-slate-500">Descrizione</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-slate-500">Categoria</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-slate-500">Sostenuta da</th>
                <th className="text-right px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-slate-500">Importo</th>
                <th className="text-center px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-slate-500 w-10"></th>
                <th className="text-right px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-slate-500 w-20">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {expenses.map((expense) => (
                <tr key={expense.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap tabular-nums">
                    {new Date(expense.date).toLocaleDateString('it-IT')}
                  </td>
                  <td className="px-4 py-2.5 text-slate-800 font-medium">
                    <div>{expense.description}</div>
                    {expense.notes && (
                      <div className="text-xs text-slate-400 mt-0.5">{expense.notes}</div>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <Badge variant="neutral" size="sm">
                      {EXPENSE_CATEGORY_LABEL[expense.category]}
                    </Badge>
                  </td>
                  <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">
                    {expense.incurredBy ? (
                      <div>{expense.incurredBy.firstName} {expense.incurredBy.lastName}</div>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                    {expense.createdBy && (
                      <div className="text-xs text-slate-400 mt-0.5">
                        Registrata da {expense.createdBy.firstName} {expense.createdBy.lastName}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right font-semibold text-slate-900 whitespace-nowrap tabular-nums">
                    {EUR.format(Number(expense.amount))}
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    {expense.receiptUrl && (
                      <button
                        type="button"
                        onClick={() => handleViewReceipt(expense.id)}
                        className="p-1 text-slate-400 hover:text-primary-600 transition-colors"
                        title={expense.receiptFilename ?? 'Visualizza scontrino'}
                      >
                        <Paperclip className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => handleEdit(expense)}
                        className="p-1 text-slate-400 hover:text-primary-600 transition-colors"
                        title="Modifica"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          if (await confirm({ title: 'Eliminare spesa?', message: `"${expense.description}" verrà eliminata definitivamente.`, danger: true })) {
                            deleteMutation.mutate(expense.id)
                          }
                        }}
                        disabled={deleteMutation.isPending}
                        className="p-1 text-slate-400 hover:text-danger-600 transition-colors disabled:opacity-50"
                        title="Elimina"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-50 border-t border-slate-200">
                <td colSpan={4} className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Totale Spese
                </td>
                <td className="px-4 py-2.5 text-right font-semibold text-slate-900 tabular-nums">
                  {EUR.format(totalExpenses)}
                </td>
                <td />
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  )
}

// ============================================================
// QUOTES TAB
// ============================================================

const QUOTE_STATUS_LABELS: Record<QuoteStatus, string> = {
  DRAFT: 'Bozza',
  SENT: 'Inviato',
  ACCEPTED: 'Accettato',
  REJECTED: 'Rifiutato',
  EXPIRED: 'Scaduto',
  SUPERSEDED: 'Sostituito',
}

const QUOTE_STATUS_COLORS: Record<QuoteStatus, string> = {
  DRAFT: 'bg-slate-100 text-slate-600',
  SENT: 'bg-primary-50 text-primary-700',
  ACCEPTED: 'bg-success-50 text-success-700',
  REJECTED: 'bg-danger-50 text-danger-600',
  EXPIRED: 'bg-warning-50 text-warning-600',
  SUPERSEDED: 'bg-slate-100 text-slate-600',
}

interface ProjectQuote {
  id: string
  quoteNumber: string
  title: string
  version: number
  status: QuoteStatus
  grandTotal: number
  subtotal: number
  createdAt: string
  cancelledAt?: string
  createdBy?: { id: string; firstName: string; lastName: string }
  cancelledBy?: { id: string; firstName: string; lastName: string }
  _count?: { lineItems: number }
}

function QuotesTab({ project }: { project: ProjectWithRelations }) {
  const navigate = useNavigate()

  const { data: quotes, isLoading } = useQuery<ProjectQuote[]>({
    queryKey: ['project-quotes', project.id],
    queryFn: async () => {
      const res = await quotesApi.getByProject(project.id)
      const raw = res?.data as any
      return (raw?.data ?? raw ?? []) as ProjectQuote[]
    },
    staleTime: 30_000,
  })

  if (isLoading) return <TabSkeleton rows={2} />

  if (!quotes || quotes.length === 0) {
    return (
      <div className="text-center py-12 text-slate-400">
        <FileText className="w-8 h-8 mx-auto mb-2 opacity-40" />
        <p className="text-sm">Nessun preventivo collegato a questo progetto</p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="text-sm text-slate-500">
        {quotes.length} {quotes.length === 1 ? 'preventivo' : 'preventivi'} collegati
      </div>
      {quotes.map((q) => (
        <button
          key={q.id}
          type="button"
          onClick={() => navigate(`/preventivi/${q.id}`)}
          className="w-full text-left p-4 bg-white rounded-2xl border border-slate-200 shadow-card hover:border-slate-300 transition-colors"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-mono text-slate-400">{q.quoteNumber}</span>
                <span className="text-xs font-medium text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                  v{q.version}
                </span>
                <span className={cn('px-2 py-0.5 text-xs font-medium rounded-full', QUOTE_STATUS_COLORS[q.status])}>
                  {QUOTE_STATUS_LABELS[q.status]}
                </span>
              </div>
              <p className="text-sm font-medium text-slate-900 mt-1 truncate">{q.title}</p>
              <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                {q.createdBy && (
                  <span>Creato da {q.createdBy.firstName} {q.createdBy.lastName}</span>
                )}
                <span>{new Date(q.createdAt).toLocaleDateString('it-IT')}</span>
                {q._count && (
                  <span>{q._count.lineItems} {q._count.lineItems === 1 ? 'voce' : 'voci'}</span>
                )}
              </div>
              {q.status === 'SUPERSEDED' && q.cancelledBy && q.cancelledAt && (
                <p className="text-xs text-slate-500 mt-1">
                  Annullato da {q.cancelledBy.firstName} {q.cancelledBy.lastName} il {new Date(q.cancelledAt).toLocaleDateString('it-IT')}
                </p>
              )}
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-lg font-semibold text-slate-900 tabular-nums">{EUR.format(Number(q.grandTotal))}</p>
              <p className="text-xs text-slate-400">IVA incl.</p>
            </div>
          </div>
        </button>
      ))}
    </div>
  )
}

// ============================================================
// DETAILS TAB
// ============================================================

function DetailsTab({ project }: { project: ProjectWithRelations }) {
  const rows: { label: string; value: string | undefined }[] = [
    { label: 'Nome', value: project.name },
    { label: 'Stato', value: STATUS_LABEL[project.status] },
    { label: 'Tipo Progetto', value: TYPE_LABEL[project.type] },
    { label: 'Modulo', value: (project.moduleTypes ?? []).map((m: string) => MODULE_LABEL[m as keyof typeof MODULE_LABEL] ?? m).join(', ') || '-' },
    { label: 'Cliente', value: project.client?.name },
    { label: 'Responsabile', value: project.manager ? `${project.manager.firstName} ${project.manager.lastName}` : undefined },
    { label: 'Data Inizio', value: project.startDate ? new Date(project.startDate).toLocaleDateString('it-IT') : undefined },
    { label: 'Data Fine', value: project.endDate ? new Date(project.endDate).toLocaleDateString('it-IT') : undefined },
    { label: 'Budget Cliente', value: project.budget ? EUR.format(project.budget) : undefined },
    { label: 'Budget Interno', value: project.spentBudget ? EUR.format(project.spentBudget) : undefined },
    { label: 'Membri Team', value: String(project._count?.team ?? project.team?.length ?? 0) },
    { label: 'Task Totali', value: String(project._count?.tasks ?? project.tasks?.length ?? 0) },
    { label: 'Creato il', value: new Date(project.createdAt).toLocaleDateString('it-IT') },
    { label: 'Aggiornato il', value: new Date(project.updatedAt).toLocaleDateString('it-IT') },
  ]

  return (
    <Card padding="md">
      <CardHeader title="Informazioni Progetto" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 mt-3">
        {rows.map((row) => (
          <div key={row.label}>
            <p className="text-xs text-slate-400 mb-0.5">{row.label}</p>
            <p className="text-sm text-slate-800 font-medium">{row.value ?? '-'}</p>
          </div>
        ))}
      </div>
      {project.description && (
        <div className="mt-4 pt-4 border-t border-slate-100">
          <p className="text-xs text-slate-400 mb-1">Descrizione</p>
          <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{project.description}</p>
        </div>
      )}
    </Card>
  )
}

// ============================================================
// MAIN PAGE
// ============================================================

/**
 * Vista ristretta per gli utenti guest/cliente.
 * Mostra SOLO i moduli accessibili — niente budget, spese, preventivi, team, dettagli.
 * - 1 modulo accessibile  → entra direttamente nel modulo
 * - >1 moduli accessibili → schermata minimale di selezione
 * - 0 moduli accessibili  → messaggio informativo
 */
function GuestProjectView({ project }: { project: ProjectWithRelations }) {
  const navigate = useNavigate()
  const modules = ((project.moduleTypes ?? []) as string[]).filter((m) => GUEST_MODULE_TYPES.has(m))

  // Con un solo modulo accessibile, salta la selezione ed entra diretto.
  useEffect(() => {
    const only = modules.length === 1 ? modules[0] : undefined
    if (only) {
      navigate(`${MODULE_ROUTE[only] ?? '/'}?projectId=${project.id}`, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modules.length, project.id])

  return (
    <div className="space-y-6">
      {/* Back navigation */}
      <button
        onClick={() => navigate('/projects')}
        className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Torna ai Progetti
      </button>

      {/* Project header (no budget, no azioni amministrative) */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-card p-5">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-primary-50 rounded-xl flex items-center justify-center flex-shrink-0">
            <span className="text-lg font-semibold text-primary-600">{project.name.charAt(0)}</span>
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">{project.name}</h1>
            {project.client && (
              <p className="text-sm text-slate-500 mt-1">{project.client.name}</p>
            )}
          </div>
        </div>
      </div>

      {/* Selezione moduli */}
      {modules.length > 1 ? (
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">Seleziona un modulo</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {modules.map((mod) => (
              <button
                key={mod}
                onClick={() => navigate(`${MODULE_ROUTE[mod] ?? '/'}?projectId=${project.id}`)}
                className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-card hover:border-slate-300 transition-colors text-left group"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">
                    {MODULE_LABEL[mod as keyof typeof MODULE_LABEL] ?? mod}
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5">Vai al modulo</p>
                </div>
                <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-primary-600 transition-colors flex-shrink-0" />
              </button>
            ))}
          </div>
        </div>
      ) : modules.length === 0 ? (
        <Card padding="md">
          <p className="text-sm text-slate-500 text-center py-4">
            Nessun modulo disponibile per questo progetto.
          </p>
        </Card>
      ) : (
        // modules.length === 1 → redirect in corso
        <div className="flex items-center justify-center py-8">
          <Spinner size="md" label="Apertura modulo..." />
        </div>
      )}
    </div>
  )
}

const ALL_TAB_IDS: TabId[] = ['overview', 'team', 'tasks', 'expenses', 'quotes', 'details', 'control-room']

export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab')
  const activeTab: TabId = (tabParam && (ALL_TAB_IDS as string[]).includes(tabParam))
    ? (tabParam as TabId)
    : 'overview'
  const setActiveTab = (tab: TabId) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (tab === 'overview') next.delete('tab')
      else next.set('tab', tab)
      return next
    }, { replace: true })
  }
  const [showEditModal, setShowEditModal] = useState(false)

  const user = useAuthStore((state) => state.user)
  const canDelete = (user?.role?.level ?? 99) <= 2

  const {
    data: project,
    isLoading,
    isError,
    error,
  } = useQuery<ProjectWithRelations>({
    queryKey: ['project', id],
    queryFn: async () => {
      const res = await projectsApi.getById(id!)
      // Response interceptor auto-unwraps envelope, so res.data is the Project
      return res.data as ProjectWithRelations
    },
    enabled: Boolean(id),
    retry: 1,
  })

  const queryClient = useQueryClient()
  const setActiveProject = useProjectContextStore((s) => s.setActiveProject)
  const clearProject = useProjectContextStore((s) => s.clearProject)

  // Serializza moduleTypes per usarlo come dipendenza stabile
  const moduleTypesKey = project?.moduleTypes?.join(',') ?? ''

  useEffect(() => {
    if (project) {
      setActiveProject({ id: project.id, name: project.name, moduleTypes: project.moduleTypes ?? [] })
    }
    return () => clearProject()
  }, [project?.id, moduleTypesKey])

  function handleEditSuccess() {
    setShowEditModal(false)
    void queryClient.invalidateQueries({ queryKey: ['project', id] })
  }

  const deleteMutation = useMutation({
    mutationFn: () => apiClient.delete<unknown>(`/projects/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
      navigate('/projects')
    },
  })

  const statusMutation = useMutation({
    mutationFn: (status: string) => projectsApi.updateStatus(id!, status),
    onSuccess: () => {
      toast.success('Stato aggiornato')
      void queryClient.invalidateQueries({ queryKey: ['project', id] })
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
    },
    onError: () => {
      // Rollback visivo: la query non viene invalidata, quindi il <select>
      // (controllato da project.status) torna automaticamente al valore precedente.
      void queryClient.invalidateQueries({ queryKey: ['project', id] })
    },
  })

  // Full-page loading skeleton
  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-5 bg-slate-100 rounded w-40" />
        <div className="h-28 bg-slate-100 rounded-lg" />
        <div className="h-10 bg-slate-100 rounded w-full" />
        <div className="space-y-3">
          <div className="h-16 bg-slate-100 rounded-lg" />
          <div className="h-16 bg-slate-100 rounded-lg" />
        </div>
      </div>
    )
  }

  // Full-page error
  if (isError || !project) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate('/projects')}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Torna ai Progetti
        </button>
        <ErrorBanner message={isError ? getApiErrorMessage(error) : 'Progetto non trovato.'} />
      </div>
    )
  }

  // Guest/cliente: vista ristretta ai soli moduli accessibili.
  if (user?.isGuest) {
    return <GuestProjectView project={project} />
  }

  const isOverdue =
    project.endDate &&
    new Date(project.endDate) < new Date() &&
    project.status !== 'COMPLETED' &&
    project.status !== 'CANCELLED'

  return (
    <div className="space-y-6">
      {/* Back navigation */}
      <button
        onClick={() => navigate('/projects')}
        className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Torna ai Progetti
      </button>

      {/* Project header card */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-card p-5">
        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
          {/* Project icon + title */}
          <div className="flex items-start gap-4 flex-1 min-w-0">
          <div className="w-12 h-12 bg-primary-50 rounded-xl flex items-center justify-center flex-shrink-0">
            <span className="text-lg font-semibold text-primary-600">{project.name.charAt(0)}</span>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-semibold tracking-tight text-slate-900">{project.name}</h1>
              {((user?.role?.level ?? 99) <= 2 || project.managerId === user?.id) ? (
                <select
                  value={project.status}
                  onChange={(e) => statusMutation.mutate(e.target.value)}
                  disabled={statusMutation.isPending}
                  className="text-xs font-medium px-2.5 py-1 rounded-full border border-slate-200 bg-white cursor-pointer hover:border-slate-300 focus:outline-none focus:ring-1 focus:ring-primary-500/20 transition-colors disabled:opacity-60"
                >
                  {Object.entries(STATUS_LABEL).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              ) : (
                <Badge variant={STATUS_VARIANT[project.status]} dot>{STATUS_LABEL[project.status]}</Badge>
              )}
              <Badge variant="neutral">{TYPE_LABEL[project.type]}</Badge>
              {isOverdue && <Badge variant="danger">Scaduto</Badge>}
            </div>
            <div className="flex items-center gap-4 mt-2 text-sm text-slate-500 flex-wrap">
              {project.client && (
                <Link
                  to={`/clients/${project.client.id}`}
                  className="hover:text-primary-600 transition-colors"
                >
                  {project.client.name}
                </Link>
              )}
              {project.manager && (
                <span>PM: {project.manager.firstName} {project.manager.lastName}</span>
              )}
              {project.startDate && (
                <span className="tabular-nums">
                  {new Date(project.startDate).toLocaleDateString('it-IT')}
                  {project.endDate && ` - ${new Date(project.endDate).toLocaleDateString('it-IT')}`}
                </span>
              )}
            </div>
          </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
              onClick={() => setShowEditModal(true)}
            >
              <Pencil className="w-4 h-4" />
              <span className="hidden sm:inline">Modifica</span>
            </button>
            {canDelete && (
              <button
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-danger-600 bg-white border border-slate-200 rounded-lg hover:bg-danger-50 hover:border-danger-300 transition-colors"
                onClick={async () => {
                  if (await confirm({ title: 'Eliminare progetto?', message: 'Questa azione è irreversibile. Il progetto e i dati collegati verranno eliminati.', confirmLabel: 'Elimina progetto', danger: true })) {
                    deleteMutation.mutate()
                  }
                }}
                disabled={deleteMutation.isPending}
              >
                <Trash2 className="w-4 h-4" />
                {deleteMutation.isPending ? 'Eliminazione...' : 'Elimina'}
              </button>
            )}
          </div>
        </div>
      </div>

      {deleteMutation.isError && (
        <ErrorBanner message={getApiErrorMessage(deleteMutation.error)} />
      )}

      {/* Tabs */}
      {(() => {
        const EVENT_TYPES: string[] = [
          'EVENT_ORGANIZATION', 'FAIR_STAND', 'CONFERENCE', 'PRODUCT_LAUNCH',
          'GALA_DINNER', 'WORKSHOP_SEMINAR', 'CORPORATE_EVENT', 'PRESS_CONFERENCE', 'ROADSHOW',
        ]
        const isEventProject = EVENT_TYPES.includes(project.type)
        const tabs = isEventProject
          ? [...TABS, { id: 'control-room' as TabId, label: 'Control Room', icon: Radio }]
          : TABS
        return (
          <div className="flex items-center gap-1 border-b border-slate-200 overflow-x-auto scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'px-3.5 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap',
                  activeTab === tab.id
                    ? 'border-primary-600 text-primary-700'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )
      })()}

      {/* Tab content */}
      <div>
        {activeTab === 'overview' && <OverviewTab project={project} />}
        {activeTab === 'team' && <TeamTab project={project} />}
        {activeTab === 'tasks' && <TasksTab project={project} />}
        {activeTab === 'expenses' && <ExpensesTab project={project} />}
        {activeTab === 'quotes' && <QuotesTab project={project} />}
        {activeTab === 'details' && <DetailsTab project={project} />}
        {activeTab === 'control-room' && <ControlRoomTab project={project} />}
      </div>

      {/* Edit modal */}
      {showEditModal && (
        <ProjectFormModal
          project={project}
          onClose={() => setShowEditModal(false)}
          onSuccess={handleEditSuccess}
        />
      )}
    </div>
  )
}
